import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { useAppStore } from '@src/app/context'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import { useCallContext } from '@src/app/voice/ongoing-call/context/useCallContext'
import { ExternalParticipantBanner } from '@src/app/voice/ongoing-call/external-participant-banner'
import { MessageTextInput } from '@src/app/voice/ongoing-call/message-text-input'
import Button from '@src/component/ButtonV2'
import PhoneNumberSelector from '@src/component/phone-number-selector/PhoneNumberSelector'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'

import * as styles from './CallTransferSelector.css'

const CallTransferSelector = () => {
  const { transferController: controller, openConfirmationModal, call } = useCallContext()
  const { inboxes, toast } = useAppStore()
  const { blurred } = useCallsDialogContext()
  const phoneNumbers = inboxes.phoneNumbers

  const [phoneNumber, setPhoneNumber] = useState(call.phoneNumber || phoneNumbers[0])

  const handleTransfer = () => {
    // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
    controller.fromPhoneNumberId = controller.hasExternalSelections
      ? phoneNumber?.id ?? null
      : null
    controller.handleTransfer().catch(toast.showError)
  }

  const handlePhoneNumberSelection = (phoneNumberIdForExternalParticipant: string) => {
    const number = phoneNumbers.find((p) => p.id === phoneNumberIdForExternalParticipant)
    if (number) {
      setPhoneNumber(number)
    }
  }

  useKeyboardShortcuts({
    name: 'CallTransfer',
    node:
      controller.messageFocused && controller.messageRef?.current
        ? controller.messageRef.current
        : controller.focusedNode,
    filter: (_, e) => !e.defaultPrevented,
    handler: (shortcut, event) => {
      if (shortcut === 'Escape' && !openConfirmationModal) {
        controller.handleBack()
        event.preventDefault()
      }
    },
    dep: [controller],
  })

  return (
    <div className={styles.root}>
      <div className={cx(styles.selector, { [styles.selectorBlurred]: blurred })}>
        <PhoneNumberSelector controller={controller} overlayThemeKey="dark" />
      </div>
      <div className={styles.controls}>
        {controller.hasInternalSelections ? (
          <MessageTextInput controller={controller} />
        ) : (
          <div style={{ flexGrow: 1 }}></div>
        )}
        {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
        <Button
          disabled={
            controller.selections.length === 0 || controller.maxMessageLengthReached
          }
          height={36}
          onClick={handleTransfer}
          className={blurred ? styles.buttonBlurred : undefined}
        >
          Transfer
        </Button>
      </div>
      <ExternalParticipantBanner
        controller={controller}
        // @ts-expect-error unchecked index access
        phoneNumber={phoneNumber}
        showChange={phoneNumbers.length > 1}
        onChange={handlePhoneNumberSelection}
      />
    </div>
  )
}

export default observer(CallTransferSelector)
